import React, { useState } from 'react';
import { PageHeader } from '../components/ui';

export default function ApiDocsPage() {
  const [selectedEndpoint, setSelectedEndpoint] = useState<string | null>(null);
  const apiBaseUrl = window.location.origin;

  const endpoints = [
    {
      category: 'Repositories',
      items: [
        {
          id: 'list-repos',
          method: 'GET',
          path: '/api/v1/repos',
          description: 'List all accessible repositories with freeze status',
          example: `curl -H "Authorization: Bearer YOUR_TOKEN" \\
  ${apiBaseUrl}/api/v1/repos`,
          response: {
            repositories: [
              {
                id: 12345,
                name: 'my-repo',
                owner: 'my-org',
                frozen: false,
                prCount: 3,
                scheduleCount: 1,
              },
            ],
          },
        },
        {
          id: 'get-repo',
          method: 'GET',
          path: '/api/v1/repos/:owner/:repo',
          description: 'Get detailed status for a specific repository',
          example: `curl -H "Authorization: Bearer YOUR_TOKEN" \\
  ${apiBaseUrl}/api/v1/repos/my-org/my-repo`,
          response: {
            owner: 'my-org',
            repo: 'my-repo',
            frozen: false,
            prCount: 3,
            scheduleCount: 1,
          },
        },
        {
          id: 'freeze-repo',
          method: 'POST',
          path: '/api/v1/repos/:owner/:repo/freeze',
          description:
            'Freeze a repository to block merges. Optionally specify a branch (defaults to default branch)',
          example: `curl -X POST \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"branch": "main"}' \\
  ${apiBaseUrl}/api/v1/repos/my-org/my-repo/freeze`,
          response: {
            success: true,
            message: 'Repository frozen successfully',
          },
        },
        {
          id: 'unfreeze-repo',
          method: 'POST',
          path: '/api/v1/repos/:owner/:repo/unfreeze',
          description:
            'Unfreeze a repository to allow merges. Optionally specify a branch (defaults to default branch)',
          example: `curl -X POST \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"branch": "main"}' \\
  ${apiBaseUrl}/api/v1/repos/my-org/my-repo/unfreeze`,
          response: {
            success: true,
            message: 'Repository unfrozen successfully',
          },
        },
        {
          id: 'repo-history',
          method: 'GET',
          path: '/api/v1/repos/:owner/:repo/history',
          description: 'Get freeze/unfreeze history for a repository',
          example: `curl -H "Authorization: Bearer YOUR_TOKEN" \\
  ${apiBaseUrl}/api/v1/repos/my-org/my-repo/history`,
          response: {
            history: [
              {
                id: 'abc123',
                action: 'FREEZE',
                performedBy: 'username',
                performedAt: '2025-09-30T10:00:00Z',
              },
            ],
          },
        },
        {
          id: 'global-history',
          method: 'GET',
          path: '/api/v1/repos/history',
          description:
            'Get recent freeze/unfreeze history across all repositories. Supports pagination with limit and before query parameters',
          example: `curl -H "Authorization: Bearer YOUR_TOKEN" \\
  "${apiBaseUrl}/api/v1/repos/history?limit=20&before=2025-09-30T10:00:00Z"`,
          response: {
            success: true,
            history: [
              {
                id: 'abc123',
                type: 'freeze',
                action: 'FREEZE',
                performedBy: 'username',
                performedAt: '2025-09-30T10:00:00Z',
                repository: {
                  owner: 'my-org',
                  repo: 'my-repo',
                },
              },
            ],
          },
        },
        {
          id: 'track-repo',
          method: 'POST',
          path: '/api/v1/repos/track',
          description: 'Track a repository for freeze management',
          example: `curl -X POST \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "owner": "my-org",
    "repo": "my-repo"
  }' \\
  ${apiBaseUrl}/api/v1/repos/track`,
          response: {
            success: true,
            repository: {
              owner: 'my-org',
              repo: 'my-repo',
              frozen: false,
            },
          },
        },
        {
          id: 'get-exemptions',
          method: 'GET',
          path: '/api/v1/repos/:owner/:repo/exemptions',
          description:
            'Get PR exemptions for a repository. Optional branch query parameter to get exemptions for specific branch',
          example: `curl -H "Authorization: Bearer YOUR_TOKEN" \\
  "${apiBaseUrl}/api/v1/repos/my-org/my-repo/exemptions?branch=main"`,
          response: {
            exemptPRs: ['123', '456'],
            branch: 'main',
          },
        },
        {
          id: 'update-exemptions',
          method: 'PUT',
          path: '/api/v1/repos/:owner/:repo/exemptions',
          description:
            'Update PR exemptions for a repository. Specify exemptPRs as array of PR numbers and optional branch',
          example: `curl -X PUT \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "exemptPRs": ["123", "456"],
    "branch": "main"
  }' \\
  ${apiBaseUrl}/api/v1/repos/my-org/my-repo/exemptions`,
          response: {
            success: true,
            exemptPRs: ['123', '456'],
          },
        },
        {
          id: 'bulk-freeze',
          method: 'POST',
          path: '/api/v1/repos/freeze/bulk',
          description: 'Freeze multiple repositories at once',
          example: `curl -X POST \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "repos": [
      {"owner": "my-org", "repo": "repo-1"},
      {"owner": "my-org", "repo": "repo-2"}
    ]
  }' \\
  ${apiBaseUrl}/api/v1/repos/freeze/bulk`,
          response: {
            success: true,
            results: [
              {
                owner: 'my-org',
                repo: 'repo-1',
                success: true,
              },
              {
                owner: 'my-org',
                repo: 'repo-2',
                success: true,
              },
            ],
            summary: {
              total: 2,
              succeeded: 2,
              failed: 0,
            },
          },
        },
        {
          id: 'bulk-unfreeze',
          method: 'POST',
          path: '/api/v1/repos/unfreeze/bulk',
          description: 'Unfreeze multiple repositories at once',
          example: `curl -X POST \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "repos": [
      {"owner": "my-org", "repo": "repo-1"},
      {"owner": "my-org", "repo": "repo-2"}
    ]
  }' \\
  ${apiBaseUrl}/api/v1/repos/unfreeze/bulk`,
          response: {
            success: true,
            results: [
              {
                owner: 'my-org',
                repo: 'repo-1',
                success: true,
              },
              {
                owner: 'my-org',
                repo: 'repo-2',
                success: true,
              },
            ],
            summary: {
              total: 2,
              succeeded: 2,
              failed: 0,
            },
          },
        },
      ],
    },
    {
      category: 'Schedules',
      items: [
        {
          id: 'list-schedules',
          method: 'GET',
          path: '/api/v1/schedules',
          description: 'List all freeze schedules for your organization',
          example: `curl -H "Authorization: Bearer YOUR_TOKEN" \\
  ${apiBaseUrl}/api/v1/schedules`,
          response: {
            schedules: [
              {
                id: 'schedule-1',
                name: 'Weekend Freeze',
                scheduleType: 'WEEKLY',
                startTime: '17:00',
                endTime: '09:00',
              },
            ],
          },
        },
        {
          id: 'repo-schedules',
          method: 'GET',
          path: '/api/v1/schedules/repos/:owner/:repo',
          description: 'List schedules for a specific repository',
          example: `curl -H "Authorization: Bearer YOUR_TOKEN" \\
  ${apiBaseUrl}/api/v1/schedules/repos/my-org/my-repo`,
          response: [
            {
              id: 'schedule-1',
              name: 'Weekend Freeze',
              scheduleType: 'WEEKLY',
              startTime: '17:00',
              endTime: '09:00',
              timezone: 'America/New_York',
              daysOfWeek: '5,6',
            },
          ],
        },
        {
          id: 'get-schedule',
          method: 'GET',
          path: '/api/v1/schedules/:id',
          description: 'Get details for a specific schedule',
          example: `curl -H "Authorization: Bearer YOUR_TOKEN" \\
  ${apiBaseUrl}/api/v1/schedules/schedule-1`,
          response: {
            schedule: {
              id: 'schedule-1',
              name: 'Weekend Freeze',
              scheduleType: 'WEEKLY',
              startTime: '17:00',
              endTime: '09:00',
            },
          },
        },
        {
          id: 'create-schedule',
          method: 'POST',
          path: '/api/v1/schedules',
          description:
            'Create a new freeze schedule. Use scheduleType "WEEKLY" for recurring, "ONCE" for one-time. Optional branchName to target specific branch',
          example: `curl -X POST \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "owner": "my-org",
    "repo": "my-repo",
    "name": "Weekend Freeze",
    "scheduleType": "WEEKLY",
    "startTime": "17:00",
    "endTime": "09:00",
    "timezone": "America/New_York",
    "daysOfWeek": [5, 6],
    "branchName": "main"
  }' \\
  ${apiBaseUrl}/api/v1/schedules`,
          response: {
            schedule: {
              id: 'schedule-1',
              name: 'Weekend Freeze',
            },
          },
        },
        {
          id: 'update-schedule',
          method: 'PATCH',
          path: '/api/v1/schedules/:id',
          description: 'Update an existing schedule',
          example: `curl -X PATCH \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "enabled": false,
    "startTime": "18:00"
  }' \\
  ${apiBaseUrl}/api/v1/schedules/schedule-1`,
          response: {
            schedule: {
              id: 'schedule-1',
              name: 'Weekend Freeze',
              enabled: false,
              startTime: '18:00',
            },
          },
        },
        {
          id: 'delete-schedule',
          method: 'DELETE',
          path: '/api/v1/schedules/:id',
          description: 'Delete a freeze schedule',
          example: `curl -X DELETE \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  ${apiBaseUrl}/api/v1/schedules/schedule-1`,
          response: {
            success: true,
            message: 'Schedule deleted successfully',
          },
        },
      ],
    },
  ];

  const allEndpoints = endpoints.flatMap(cat => cat.items);
  const selected = allEndpoints.find(e => e.id === selectedEndpoint);

  return (
    <div className="page">
      <PageHeader
        title="API Documentation"
        subtitle="RESTful API for programmatic access"
        stats={
          <div style={{ display: 'flex', gap: '12px' }}>
            <a
              href="#authentication"
              className="btn btn-secondary"
              style={{ fontSize: '13px', padding: '8px 16px' }}
              onClick={e => {
                e.preventDefault();
                setSelectedEndpoint(null);
                document.getElementById('authentication')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              <i className="fa-solid fa-key"></i>
              <span>Authentication</span>
            </a>
            <a
              href="#errors"
              className="btn btn-secondary"
              style={{ fontSize: '13px', padding: '8px 16px' }}
              onClick={e => {
                e.preventDefault();
                setSelectedEndpoint(null);
                document.getElementById('errors')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              <i className="fa-solid fa-triangle-exclamation"></i>
              <span>Error Handling</span>
            </a>
            <a
              href="#webhooks"
              className="btn btn-secondary"
              style={{ fontSize: '13px', padding: '8px 16px' }}
              onClick={e => {
                e.preventDefault();
                setSelectedEndpoint(null);
                document.getElementById('webhooks')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              <i className="fa-solid fa-arrow-right-arrow-left"></i>
              <span>Webhooks</span>
            </a>
          </div>
        }
      />

      <div className="page-content" style={{ display: 'flex', gap: '24px' }}>
        {/* Sidebar Navigation */}
        <div style={{ flex: '0 0 250px' }}>
          <div className="api-docs-sidebar">
            {endpoints.map(category => (
              <div key={category.category} className="docs-section">
                <h3>{category.category}</h3>
                {category.items.map(endpoint => (
                  <a
                    key={endpoint.id}
                    href={`#${endpoint.id}`}
                    className={`docs-link ${selectedEndpoint === endpoint.id ? 'active' : ''}`}
                    onClick={e => {
                      e.preventDefault();
                      setSelectedEndpoint(endpoint.id);
                      // Scroll to top of page
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                  >
                    <span className={`method-badge ${endpoint.method.toLowerCase()}`}>
                      {endpoint.method}
                    </span>
                    {endpoint.path.split('/').pop()}
                  </a>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Endpoint Details */}
          {selected ? (
            <section id={selected.id} className="endpoint-detail">
              <div className="endpoint-header">
                <span className={`method-badge ${selected.method.toLowerCase()}`}>
                  {selected.method}
                </span>
                <code className="endpoint-path">{selected.path}</code>
              </div>

              <p className="endpoint-description">{selected.description}</p>

              <h3>Example Request</h3>
              <div className="code-block">
                <pre>{selected.example}</pre>
              </div>

              <h3>Example Response</h3>
              <div className="code-block">
                <pre>{JSON.stringify(selected.response, null, 2)}</pre>
              </div>
            </section>
          ) : (
            <>
              {/* Getting Started Sections */}
              <section id="authentication" className="docs-content-section">
                <h2>Authentication</h2>
                <p>
                  All API requests require authentication using a Bearer token in the Authorization
                  header.
                </p>

                <h3>Creating an API Token</h3>
                <ol>
                  <li>
                    Go to <strong>Settings → API Tokens</strong> in the dashboard
                  </li>
                  <li>
                    Click <strong>"Create API Token"</strong>
                  </li>
                  <li>Choose a name and expiration period (up to 90 days)</li>
                  <li>Copy the token immediately - you won't be able to see it again!</li>
                </ol>

                <h3>Using Your Token</h3>
                <div className="code-block">
                  <pre>{`curl -H "Authorization: Bearer YOUR_TOKEN" \\
  ${apiBaseUrl}/api/v1/repos`}</pre>
                </div>

                <h3>Token Permissions</h3>
                <p>
                  API tokens have full access to all repositories and operations within their
                  organization. Tokens are scoped to a single organization selected during creation.
                </p>

                <h3>Token Security</h3>
                <ul>
                  <li>
                    Tokens expire after the configured period (default: 30 days, max: 90 days)
                  </li>
                  <li>Tokens can be revoked at any time from the dashboard</li>
                  <li>Store tokens securely - treat them like passwords</li>
                  <li>Never commit tokens to version control</li>
                </ul>
              </section>

              <section id="errors" className="docs-content-section">
                <h2>Error Handling</h2>
                <p>The API uses standard HTTP status codes:</p>
                <ul>
                  <li>
                    <code>200 OK</code> - Request succeeded
                  </li>
                  <li>
                    <code>201 Created</code> - Resource created successfully
                  </li>
                  <li>
                    <code>400 Bad Request</code> - Invalid request parameters
                  </li>
                  <li>
                    <code>401 Unauthorized</code> - Missing or invalid authentication
                  </li>
                  <li>
                    <code>403 Forbidden</code> - Insufficient permissions or access denied
                  </li>
                  <li>
                    <code>404 Not Found</code> - Resource not found
                  </li>
                  <li>
                    <code>500 Internal Server Error</code> - Server error
                  </li>
                </ul>

                <h3>Error Response Format</h3>
                <div className="code-block">
                  <pre>{`{
  "error": "Repository limit reached. Upgrade to Pro for unlimited repositories.",
  "requiresUpgrade": true
}`}</pre>
                </div>

                <h3>Security Note</h3>
                <p>
                  For security reasons, some endpoints may return generic error messages like{' '}
                  <code>"Forbidden"</code> without additional details. This prevents information
                  disclosure about the API's internal structure.
                </p>
              </section>

              <section id="webhooks" className="docs-content-section">
                <h2>Webhooks</h2>
                <p>
                  Webhooks allow you to receive real-time notifications when repositories are frozen
                  or unfrozen. Configure webhooks in <strong>Settings → Webhooks</strong>.
                </p>

                <h3>Event Types</h3>
                <ul>
                  <li>
                    <code>repository.frozen</code> - Triggered when a repository is frozen
                  </li>
                  <li>
                    <code>repository.unfrozen</code> - Triggered when a repository is unfrozen
                  </li>
                  <li>
                    <code>webhook.test</code> - Test event sent when testing a webhook
                  </li>
                </ul>

                <h3>Webhook Payload</h3>
                <p>
                  Webhook events are sent as HTTP POST requests with a JSON payload. The{' '}
                  <code>X-Yukon-Event</code> header contains the event type.
                </p>
                <div className="code-block">
                  <pre>{`{
  "type": "repository.frozen",
  "repository": {
    "owner": "my-org",
    "repo": "my-repo",
    "full_name": "my-org/my-repo"
  },
  "action": {
    "type": "freeze",
    "performedBy": "username",
    "timestamp": "2025-10-07T10:30:00.000Z"
  },
  "organization": {
    "id": "org_abc123",
    "name": "My Organization"
  }
}`}</pre>
                </div>

                <h3>Webhook Headers</h3>
                <ul>
                  <li>
                    <code>Content-Type: application/json</code>
                  </li>
                  <li>
                    <code>X-Yukon-Signature: sha256=...</code> - HMAC signature for verification
                  </li>
                  <li>
                    <code>X-Yukon-Event: repository.frozen</code> - Event type
                  </li>
                  <li>
                    <code>User-Agent: Yukon-Webhooks/1.0</code>
                  </li>
                </ul>

                <h3>Signature Verification</h3>
                <p>
                  Verify webhook authenticity by validating the HMAC-SHA256 signature in the{' '}
                  <code>X-Yukon-Signature</code> header using your webhook secret. Create an HMAC
                  hash of the request body using SHA-256 and compare it with the signature.
                </p>

                <h3>Webhook Delivery</h3>
                <ul>
                  <li>Webhooks are delivered with a 10-second timeout</li>
                  <li>Response status codes 200-299 are considered successful</li>
                  <li>Failed deliveries are logged and can be viewed in Settings</li>
                  <li>There are no automatic retries (manual testing available)</li>
                </ul>

                <h3>Testing Webhooks</h3>
                <p>
                  You can test your webhook endpoint from <strong>Settings → Webhooks</strong> by
                  clicking the test button (paper plane icon). This sends a test event with mock
                  data.
                </p>

                <h3>Security Best Practices</h3>
                <ul>
                  <li>Always verify webhook signatures before processing events</li>
                  <li>Use HTTPS endpoints for webhook URLs</li>
                  <li>Keep your webhook secret secure - never commit it to version control</li>
                  <li>Implement rate limiting on your webhook endpoint</li>
                  <li>Return a 200 status code quickly, then process asynchronously</li>
                </ul>
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
